import {
    isInsideArea,
    metaKey,
    getMRNATextBases,
    aminoSequenceSplitted,
    getAminoAcidVariant,
    arrowHeadTrimmer,
    attributeAnnotations,
    scaleAnnotationSize,
    alignBpPosition,
    fixRangeBp,
    truncateAnnotationLabel,
    getBasePairs,
} from "../editor/utils";

import {
    restrictionEnzymeCut,
    annotationPath,
    codonPath,
    annotationTextPath
} from "./linearShape";

import {
    aminoSequenceColors,
    nomenclatures,
    thresholdLevels,
    severityToColor,
    EDITOR_TYPES,
    colors,
    annotationCodes,
    dnaStatusLabels
} from "./dictionaries";

import {
    paddingLinearEditor,
    SLOT_HEIGHT,
    STEP
} from "./constants";


import {renderVirtualPopper} from "../utils/virtualPopper";

import {line} from "d3-shape";
import {drag} from "d3-drag";
import {zoom as scroll} from "d3-zoom";
import {select} from "d3-selection";

const d3 = {select, drag, scroll, line}

const viewMixins = {
    addMainContainerEvents() {
        this.mainBox
            .on("contextmenu", (e) => {
                e.preventDefault();
                e.stopPropagation();
            })
            .call(d3.drag()
                .on("start drag end", e => this.eventOnCreateBrush(e))
            )
            .call(d3.scroll()
                .on("start zoom end", e => {
                    if (metaKey(e.sourceEvent, this)) {
                        e.preventDefault;
                        if (e.type === "end") {
                            const newBrushSize = this.setBrushSize();
                            this.EventsTrigger("updateBrush", newBrushSize)

                            this.EventsTrigger("showBrush")
                            this.showBrush();

                        } else if (e.type === "start") {
                            this.startScrolling();
                        } else {
                            this.zoom += (e.sourceEvent.wheelDelta < 0 ? .5 : -.5);
                            if (this.zoom >= 4 && this.zoom <= 20) {
                                this.setFontWidth(this.zoom)
                                this.EventsTrigger("setFontWidth", this.zoom)
                            } else {
                                this.zoom = this.zoom < 4 ? 4 : 20;
                            }
                        }
                    } else {
                        if (e.sourceEvent.detail < 2) {
                            this.scroll(e)
                            this.hideContextMenu()
                        }
                    }
                })
            )
    },

    setBrushSize() {
        const newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }

        this.brush.setParameters(newBrushSize);
        return newBrushSize;
    },

    scroll(wheelEvent) {
        if (wheelEvent.sourceEvent && wheelEvent.type === "zoom") {
            let step = Math.ceil((1 / 100) * this.maxBasePairsInAreaView);

            if (
                (this.offsetScroll.current < paddingLinearEditor) ||
                (this.offsetScroll.current > (this.sequenceLength - this.maxBasePairsInAreaView - paddingLinearEditor))) {

                step = 1;
            }
            const scroll = wheelEvent.sourceEvent.wheelDelta < 0 ? -step : step;

            this.setScroll(scroll);
            this.EventsTrigger("setScroll", scroll);

            this.slide(wheelEvent.type);
            this.EventsTrigger("slide", wheelEvent.type);
        } else {
            if (wheelEvent.type === "start") {
                this.startScrolling()

                this.EventsTrigger("slide", wheelEvent.type);
            } else {
                const newBrushSize = this.setBrushSize();
                this.brush.show();

                this.EventsTrigger("showBrush")
                this.EventsTrigger("updateBrush", newBrushSize)

                this.offsetScroll.step = 0;
                this.middleArea = this.offsetScroll.current + Math.round(this.maxBasePairsInAreaView / 2);

                this.slide(wheelEvent.type);
                this.EventsTrigger("slide", wheelEvent.type);
            }
        }
    },

    setScroll(scroll) {
        const min = 0
        const max = this.sequenceLength - this.maxBasePairsInAreaView;

        this.offsetScroll.current -= scroll;
        this.offsetScroll.step -= scroll;
        if (this.offsetScroll.current < min || this.offsetScroll.current > max) {
            if (this.offsetScroll.current < min) {
                this.offsetScroll.current = min
            } else {
                this.offsetScroll.current = max
            }
        } else {
            this.edges.left -= scroll;
            this.edges.right -= scroll;
        }
    },


    getEdges(maxBasePairsInAreaView, sequenceLength) {
        let leftEdge = this.offsetScroll.current;
        let rightEdge = leftEdge + maxBasePairsInAreaView;

        if (leftEdge < 0 || rightEdge > sequenceLength) {
            if (leftEdge < 0) {
                this.offsetScroll.current = 0;
                leftEdge = this.offsetScroll.current;
                rightEdge = leftEdge + maxBasePairsInAreaView;
            } else {
                this.offsetScroll.current = sequenceLength - maxBasePairsInAreaView;
                leftEdge = this.offsetScroll.current;
                rightEdge = sequenceLength;
            }
        }

        this.edges = {
            left: leftEdge,
            right: rightEdge,
        }
    },

    selectRange(data) {
        this.rangeBp = {
            start: data.start,
            end: data.end
        };

        this.setBrushSize();
    },

    enableTooltip() {
        const elements = document.querySelectorAll('[data-toggle="virtual-popper"]')
        renderVirtualPopper(elements);
    },
}

const slotMixins = {
    drawSlot(container, slot, slotStart, slotEnd, dnaStatusClass, height, label) {
        const ARROW_LENGTH = 6;
        const [start, end, arrowSize, direction] = scaleAnnotationSize(slotStart, slotEnd, slot.direction, ARROW_LENGTH, this.fontBoxWidth, this.fontSizeBase, this.sequenceLength, true);
        const OPACITY_SHADE = .6;
        const DNASTATUS_SLOT = SLOT_HEIGHT * (2/3) - (STEP /2);
        const HIGHLIGHT_SLOT = SLOT_HEIGHT * (1/3);
        const OFFSET_STEP = 5;

        container
            .append("path")
            .attr("class", "editor slot")
            .attr("d", annotationPath(0, DNASTATUS_SLOT, start, end, slot.direction, arrowSize, this.fontBoxWidth))
            .attr('data-toggle', 'virtual-popper')
            .attr("data-virtual-popper-content", label)
            .style("opacity", OPACITY_SHADE)
            .style("stroke-width", 0)

        // edge
        container
            .append("line")
            .attr("class", "slot")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", "100%")
            .style("stroke-dasharray", ("3, 3"));

        // is obsolete, is public, is shared
        if (dnaStatusClass) {
            container.append("path")
                .attr("class", "slot " + dnaStatusClass)
                .attr('data-toggle', 'virtual-popper')
                .attr("data-virtual-popper-content", dnaStatusLabels[dnaStatusClass])
                .attr("d", `M${start * this.fontBoxWidth},${DNASTATUS_SLOT + HIGHLIGHT_SLOT - OFFSET_STEP}                    
                    L${(end * this.fontBoxWidth)},${DNASTATUS_SLOT + HIGHLIGHT_SLOT - OFFSET_STEP}
                    L${(end * this.fontBoxWidth)},${HIGHLIGHT_SLOT + (OFFSET_STEP * 2)}
                    L${start * this.fontBoxWidth},${HIGHLIGHT_SLOT + (OFFSET_STEP * 2)}
                z`)
                .attr('stroke-width', 0)
                .style("opacity", OPACITY_SHADE);
        }
    },
}

const annotationMixins = {
    drawPlainAnnotation(container, a, color, label, annotationHeight) {
        const arrowLength = 6;
        let [start, end, arrowSize, direction] = scaleAnnotationSize(0, a.end - a.start, a.direction, arrowLength, this.fontBoxWidth, this.fontSizeBase, this.sequenceLength);
        [start, end] = arrowHeadTrimmer(start, end, direction);

        const textAnnotation = truncateAnnotationLabel(label, Math.round(a.end - a.start), this.fontBoxWidth, this.fontSizeBase);
        const annotationCenter = (annotationHeight + this.fontHeight) / 2

        container
            .append("path")
            .attr("d", annotationPath(0, annotationHeight, start, end, direction, arrowSize, this.fontBoxWidth))
            .attr("class", "annotation annotation_" + a.pk)
            .style("fill", color)
            .style("opacity", .5)
            .style("stroke", "#000")
            .style("stroke-width", 0)

        if (annotationHeight >= this.regularSizeFont.height && textAnnotation?.length > 2) {
            const annotationPathContainer = container
                .append("path")
                .attr("id", "pathAnnotationID_" + a.pk)
                .attr("d", annotationTextPath(0, start, end, this.fontBoxWidth))
                .attr("transform", `translate(0, ${annotationHeight})`)

            if (a.error_messages && a.error_messages.length === 1) {
                annotationPathContainer
                    .style("stroke", severityToColor[a.error_messages[0].severity])
                    .style("stroke-width", 2)
            }
            if (a.role_id === annotationCodes.mRNA) {
                let translationX = 0;
                const MRNAtextBases = getMRNATextBases(a.direction, getBasePairs(this.sequence, a.start, a.end))

                MRNAtextBases.forEach(bp => {
                    translationX += this.fontBoxWidth;
                    if (this.threshold === thresholdLevels.overFontWidth) {
                        container
                            .append("text")
                            .attr("class", "font-monospace")
                            .attr("x", translationX - (this.fontBoxWidth / 2))
                            .attr("dy", annotationCenter)
                            .style("text-anchor", "middle")
                            .attr("startOffset", "0%")
                            .style('fill', colors[bp] || "#000")
                            .text(bp);
                    } else {
                        container
                            .append("rect")
                            .attr("x", translationX - this.fontBoxWidth)
                            .attr("y", 4)
                            .attr("width", this.fontBoxWidth)
                            .attr("height", this.fontHeight)
                            .style('fill', colors[bp] || "#000")
                            .style('opacity', .6);
                    }
                })
            } else {
                container
                    .append("text")
                    .attr("transform", `translate(0, -${annotationHeight})`)
                    .attr("class", "annotation annotation_" + a.pk)
                    .attr("dy", (annotationHeight + this.fontHeight) / 2)
                    .append("textPath")
                    .attr("xlink:href", "#pathAnnotationID_" + a.pk)
                    .attr("class", "wrap_text")
                    .style("text-anchor", "middle")
                    .text(textAnnotation)
                    .attr("startOffset", "50%")
            }
        }
    },

    drawCodingSequence(container, a, layer, annotationHeight) {
        const arrowLength = .4;
        const aminoSequence = aminoSequenceSplitted(a.amino_sequence, a.direction);
        const aminoSequenceLength = aminoSequence.length;
        let start = 0;
        let end = 0;
        const codonContainers = [];
        let layerStandardOpacity = this.settings.editorType === EDITOR_TYPES.libraries ? "25" : "50";
        const totalEnd = aminoSequenceLength * 3;

        aminoSequence.forEach((codon, i) => {
            end = (start + 3);
            [start, end] = arrowHeadTrimmer(start, end, a.direction, 0, totalEnd);

            if (isInsideArea(a.start + start, a.start + end, this.edges.left, this.edges.right, this.sequenceLength)) {
                const variant = getAminoAcidVariant(a.variants, i);
                const renderVariants = variant && this.settings.editorType === EDITOR_TYPES.libraries;
                const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
                const name = a.name ? a.name : "Unknown " + attributeAnnotation.name
                const pos = a.direction === 2 ? (a.amino_sequence.length - i) : i + 1;
                let label = nomenclatures[codon] + " " + (pos) + " (" + name + ")";
                let lastVariantLayer = "100";

                if (renderVariants) {
                    const $varianceLabel = document.createElement('div');
                    $varianceLabel.classList.add("list-group", "m-0")

                    const $varianceLabelID = document.createElement('div');
                    $varianceLabelID.classList.add("list-group-item", "d-flex", "justify-content-between");
                    $varianceLabelID.innerHTML = `<span class="opacity-50">ID:</span> ${nomenclatures[codon]}${pos}`

                    const $varianceLabelScheme = document.createElement('div');
                    $varianceLabelScheme.classList.add("list-group-item", "d-flex", "justify-content-between")
                    $varianceLabelScheme.innerHTML = `<span class="opacity-50">Scheme:</span> ${variant.scheme_name}`

                    const $varianceLabelCDSName = document.createElement('div');
                    $varianceLabelCDSName.classList.add("list-group-item", "d-flex", "justify-content-between")
                    $varianceLabelCDSName.innerHTML = `<span class="opacity-50 me-2">Coding sequence:</span> ${name}`


                    $varianceLabel.appendChild($varianceLabelID)
                    $varianceLabel.appendChild($varianceLabelScheme)
                    $varianceLabel.appendChild($varianceLabelCDSName)

                    label = $varianceLabel.outerHTML;
                }

                let lastLayer, aminoAcidText
                const variantLayers = [];
                const codonContainer = container
                    .append("g")
                    .attr("transform", "translate(" + (a.start * this.fontBoxWidth) + ", " + (layer) + ")")
                    .attr("class", "annotationGroup annotation annotation_" + a.pk)
                    .attr('data-toggle', 'virtual-popper')
                    .attr("data-virtual-popper-html", !!variant)
                    .attr("data-virtual-popper-content", label)
                    .datum({codon: i, codonLabel: codon})

                if (renderVariants) {
                    const layerOpacity = ["25", "50", "75"]
                    for (let i = 0; i < 3; i++) {
                        variantLayers.push(codonContainer.append("path")
                            .attr("class", "opacity-" + layerOpacity[i])
                            .attr("d", codonPath(0, annotationHeight, start, end, a.direction, arrowLength, this.fontBoxWidth, false))
                            .style("fill", aminoSequenceColors[codon])
                            .style("stroke", aminoSequenceColors[codon])
                            .style("stroke-width", 0)
                        )
                    }
                }

                lastLayer = codonContainer.append("path")
                    .attr("d", codonPath(0, annotationHeight, start, end, a.direction, arrowLength, this.fontBoxWidth, false))
                    .attr("class", "opacity-" + (variant && this.settings.editorType === EDITOR_TYPES.libraries ? lastVariantLayer : layerStandardOpacity))
                    .style("fill", aminoSequenceColors[codon])
                    .style("stroke", aminoSequenceColors[codon])
                    .style("stroke-width", 1)

                if (annotationHeight > this.fontHeight) {
                    codonContainer
                        .append("path")
                        .attr("transform", "translate(0, " + (renderVariants ? -4.5 : 0) + ")")
                        .attr("id", "pathAnnotationID_" + a.pk + "_" + i)
                        .attr("d", annotationTextPath(0, start, end, this.fontBoxWidth))
                        .style("opacity", 0)

                    aminoAcidText = codonContainer
                        .append("text")
                        .attr("class", "annotation font-monospace annotation_" + a.pk)
                        .attr("dy", (annotationHeight + this.fontHeight) / 2)
                        .append("textPath")
                        .attr("xlink:href", "#pathAnnotationID_" + a.pk + "_" + i)
                        .attr("startOffset", "50%")
                        .attr("class", "wrap_text")
                        .style("text-anchor", "middle")
                        .text(codon)
                }

                if (renderVariants) {
                    const layerPosition = [4, 1, -2];
                    for (let i = 0; i < 3; i++) {
                        variantLayers[i].attr("transform", "translate(0, " + layerPosition[i] + ")")
                    }

                    lastLayer
                        .attr("transform", "translate(0, -4.5)")
                        .style("opacity", 1)
                    aminoAcidText
                        .style("fill", "#fff")
                }

                codonContainers.push(codonContainer)
            }
            start = end;
        })
        return codonContainers;
    },
}

const fragmentsMixins = {
    drawFragmentAnnotation(container, f, color, label, annotationHeight, layer) {

    }
}

const restrictionEnzymeMixins = {
    drawRestrictionEnzyme(container, re) {
        const [top, offset, cutShape, selectedArea] = restrictionEnzymeCut(re, this.stepLevelAnnotation, this.fontBoxWidth, this.isSlotted, this.isSingleStranded)

        container
            .attr("transform", "translate(" + ((-this.offsetScroll.current + re.cut_5) * this.fontBoxWidth) + "," + this.positionsY.restrictionEnzymes + ")")

        if (this.threshold !== thresholdLevels.full) {
            container
                .append("text")
                .attr("class", "restrictionEnzymeText restEnzyme_" + re.id)
                .attr("x", 0)
                .attr("dy", -(re.level * this.annotationHeight))
                .attr("fill", "#000")
                .attr("text-anchor", "start")
                .text(re.name);

            // cutShape
            container
                .append("path")
                .attr("class", "cut_line")
                .attr("fill", "none")
                .attr("d", cutShape)

            // selected area
            container
                .append("path")
                .attr("class", "cut_range")
                .attr("d", selectedArea)
                .attr("fill", "none")
        }

        // edge
        container
            .append("line")
            .attr("class", "cut_5")
            .attr("x1", 0)
            .attr("y1", top)
            .attr("x2", 0)
            .attr("y2", offset)
    },
}

const brushMixins = {
    initBrush() {
        const [brush, brushFirstEdge, brushSecondEdge] = this.brush.create(true);

        [brushFirstEdge, brushSecondEdge].forEach(el => {
            el.call(d3.drag()
                .on("start drag end", (event) => this.eventOnDragBrushEdges(event, el))
            )
        });

        brush.call(d3.drag()
            .on("start drag end", (event) => this.eventOnDragBrush(event))
        );
    },

    /**
     * Brush Events
     */
    eventOnCreateBrush(event) {
        if (event.sourceEvent.detail < 2) {
            event.sourceEvent.preventDefault();

            let scroll = 0;
            if (event.x < 0 || event.x > this.$mainBoxContainer.width) {
                scroll = event.x < 0 ? 1 : -1;
            }

            if (
                (event.x > this.$mainBoxContainer.width &&
                    this.offsetScroll.current === (this.sequenceLength - this.maxBasePairsInAreaView)) ||
                (event.x < 0 && this.offsetScroll.current === 0)
            ) {
                scroll = 0;
            }

            this.brush.onCreateBrush(event, scroll * this.fontBoxWidth)
            let brushSize = this.brush.getParameters();

            this.EventsTrigger("updateBrush", brushSize)

            if (event.type === "end") {
                if (event.x < 0 && this.offsetScroll.current === 0 && brushSize.width > this.fontBoxWidth) {
                    brushSize = {
                        x: 0,
                        width: this.initBasePair * this.fontBoxWidth
                    }
                }
                this.endCreateBrush(brushSize, event);

            } else if (event.type === "drag") {
                if (event.x < 0 || (event.x > this.$mainBoxContainer.width)) {
                    this.setScroll(scroll);
                    this.EventsTrigger("setScroll", scroll);
                    this.slide("end");
                    this.EventsTrigger("slide", "end");
                }
            } else {
                this.initBasePair = this.offsetScroll.current + Math.round(event.x / this.fontBoxWidth);
            }
        }
    },

    eventOnDragBrush(event) {
        if (event.sourceEvent.detail < 2) {
            this.brush.onDrag(event)

            let brushSize = this.brush.getParameters();
            this.EventsTrigger("updateBrush", event)

            if (event.type === "end") {
                if (event.x === this.brush.getCurrentX()) {
                    brushSize = {
                        x: event.x,
                        width: .1
                    }
                }

                if (this.offsetScroll.current === 0 && brushSize.x < 0) {
                    brushSize = {
                        x: 0,
                        width: brushSize.width + brushSize.x
                    }
                }

                const realRangeBp = {
                    start: alignBpPosition(this.offsetScroll.current, (brushSize.x / this.fontBoxWidth)),
                    end: alignBpPosition(this.offsetScroll.current, (brushSize.x / this.fontBoxWidth) + (brushSize.width / this.fontBoxWidth))
                }
                this.EventsTrigger("currentSlot", realRangeBp);

                this.alignRangeBp(brushSize, event);
            } else if (event.type === "drag") {
                if (event.x < 0 || event.x > this.$mainBoxContainer.width) {
                    const scroll = event.x < 0 ? 1 : -1;
                    this.setScroll(scroll);
                    this.EventsTrigger("setScroll", scroll);
                    this.slide("end");
                    this.EventsTrigger("slide", "end");
                }
            }
        }
    },

    eventOnDragBrushEdges(event, selector) {
        let scroll = 0;
        if (event.x < 0 || event.x > this.$mainBoxContainer.width) {
            scroll = event.x < 0 ? 1 : -1;
        }

        if (
            (event.x > this.$mainBoxContainer.width && this.offsetScroll.current === (this.sequenceLength - this.maxBasePairsInAreaView)) ||
            (event.x < 0 && this.offsetScroll.current === 0)
        ) {
            scroll = 0;
        }

        this.brush.onDragEdges(event, selector, scroll * this.fontBoxWidth)
        let brushSize = this.brush.getParameters();
        this.EventsTrigger("updateBrush", brushSize)

        if (event.type === "end") {
            if (event.x < 0 && this.offsetScroll.current === 0 && brushSize.width > this.fontBoxWidth) {
                brushSize = {
                    x: 0,
                    width: selector.attr('class') === "firstEdge" ? this.rangeBp.end * this.fontBoxWidth : this.rangeBp.start * this.fontBoxWidth
                }
            }
            this.endDragBrushEdges(brushSize);
        } else if (event.type === "drag") {
            if (event.x < 0 || event.x > this.$mainBoxContainer.width) {
                this.setScroll(scroll);
                this.EventsTrigger("setScroll", scroll);
                this.slide("end");
                this.EventsTrigger("slide", "end");
            }
        }
    },

    alignRangeBp(brushSize, event) {
        const start = (brushSize.x / this.fontBoxWidth);
        const end = start + (brushSize.width / this.fontBoxWidth);
        let tempRangeBp

        let floatRangeBp = {
            start: alignBpPosition(this.offsetScroll.current, start),
            end: alignBpPosition(this.offsetScroll.current, end)
        }

        if (floatRangeBp.start > this.sequenceLength) {
            floatRangeBp.start = this.sequenceLength;
            floatRangeBp.end = this.sequenceLength;
        }

        if (floatRangeBp.end > this.sequenceLength) {
            floatRangeBp.end = this.sequenceLength;
        }

        if (event && event.sourceEvent?.shiftKey) {
            if (this.rangeBp.start === this.rangeBp.end) {
                this.rangeBp = fixRangeBp({
                    start: this.rangeBp.start,
                    end: Math.round(floatRangeBp.end)
                })
            } else {
                if (floatRangeBp.end > this.rangeBp.end) {
                    this.rangeBp = fixRangeBp({
                        start: this.rangeBp.start,
                        end: Math.round(floatRangeBp.end)
                    })
                } else {
                    this.rangeBp = fixRangeBp({
                        start: Math.round(floatRangeBp.end),
                        end: this.rangeBp.end
                    })
                }
            }
            tempRangeBp = this.rangeBp;
        } else {
            this.rangeBp = {
                start: Math.round(floatRangeBp.start),
                end: Math.round(floatRangeBp.end)
            }
            tempRangeBp = floatRangeBp;
        }

        let newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: this.rangeBp.start === this.rangeBp.end ? .1 : ((this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth)
        }

        if (this.rangeBp.start > this.rangeBp.end) {
            this.rangeBp.start = 0;
            newBrushSize.x = 0;
            newBrushSize.width = (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth;
        }

        this.brush.setParameters(newBrushSize)
        this.EventsTrigger("selectRange", tempRangeBp);
    },

    updateBrush(data) {
        const startSelection = Math.round(data.x / this.fontBoxWidth);
        const endSelection = startSelection + Math.round(data.width / this.fontBoxWidth);

        this.rangeBp = {
            start: Math.abs(this.offsetScroll.current + startSelection),
            end: Math.abs(this.offsetScroll.current + endSelection)
        }

        this.brush.setParameters(data);
        this.brush.deselected();

    },

    hideBrush() {
        this.brush.hide();
    },

    showBrush() {
        this.brush.show();
    },
}

export {
    viewMixins,
    slotMixins,
    annotationMixins,
    fragmentsMixins,
    restrictionEnzymeMixins,

    brushMixins,
};